import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {Observable, BehaviorSubject, EMPTY, Subscription, timer, filter, throwError, of} from 'rxjs';

import { JwtService } from './jwt.service';
import { map, distinctUntilChanged, tap, shareReplay, catchError, take } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { User, UserRegistration, UserResponseDto, LoginDto, AuthTokenResponseDto, RefreshTokenDto } from '../user.model';
import { Router } from '@angular/router';
import { apiUrl } from '../../http/api-url';

export type AuthState = 'authenticated' | 'unauthenticated' | 'unavailable' | 'loading';

/**
 * UserService - Manages authentication state for the current user.
 *
 * ## Endpoints
 *
 * This service uses GET /user (not /users/:id or /profiles/:username):
 * - GET /user → Returns the authenticated user's own data (JWT token identifies who you are)
 * - GET /profiles/:username → Different endpoint for viewing any user's public profile (see ProfileService)
 *
 * ## Auth States
 *
 * - 'loading': Initial state, checking if stored token is valid
 * - 'authenticated': Token valid, user data loaded
 * - 'unauthenticated': No token or token invalid (4XX error)
 * - 'unavailable': Server error (5XX), token kept for retry
 *
 * ## Error Handling on GET /user
 *
 * - 4XX errors (400, 401, 403, 404): Token is invalid → logout, clear token
 * - 5XX errors (500, 503) or network errors: Server is down → keep token, auto-retry
 *
 * The distinction matters: a 401 means "your token is bad" (clear it), while a 500
 * means "server is broken" (keep the token, it might work when server recovers).
 *
 * ## Auto-Retry
 *
 * When in 'unavailable' state, the service automatically retries with exponential
 * backoff: 2s → 4s → 8s → 16s → 16s → ... (capped at 16s, retries indefinitely).
 * The UI shows "Connecting..." while retrying. User can also just reload the page.
 *
 * ## Global 401 Handling
 *
 * For other endpoints (not /user), 401 errors are caught by errorInterceptor
 * which calls purgeAuth() - this handles "token expired mid-session" scenarios.
 */
@Injectable({ providedIn: 'root' })
export class UserService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());

  private authStateSubject = new BehaviorSubject<AuthState>('loading');
  public authState = this.authStateSubject.asObservable().pipe(distinctUntilChanged());

  public isAuthenticated = this.currentUser.pipe(map(user => !!user));

  private refreshInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<AuthTokenResponseDto | null>(null);


  /**
   * Synchronously get the current cached user value.
   * Returns null if not authenticated or still loading.
   */
  getCurrentUserSync(): User | null {
    return this.currentUserSubject.getValue();
  }

  private retryAttempt = 0;
  private retrySubscription: Subscription | null = null;
  private http = inject(HttpClient);
  private jwtService = inject(JwtService);
  private router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Called only at bootstrap. Tries to restore the session silently.
   * Never redirects, never throws — failure just means "not logged in".
   */
  initializeSession(): Observable<null> {
    if (!this.isBrowser) {
      return of(null);
    }
    return this.http.post<AuthTokenResponseDto>(
      apiUrl('/v1/authentication/refresh-token'),
      {},
      { withCredentials: true }
    ).pipe(
      tap(response => {
        this.setAuthFromResponse(response)
        this.refreshInProgress = false;
        this.refreshTokenSubject.next(response);
      }),
      map(() => null),
      catchError(() => {
        // No valid session — that's fine, proceed as guest/unauthenticated
        this.refreshTokenSubject.next(null);
        this.purgeAuth();
        return of(null);
      })
    );
  }


  login(credentials: LoginDto): Observable<AuthTokenResponseDto> {
     return this.http
       .post<AuthTokenResponseDto>(apiUrl('/v1/authentication/login'), credentials)
       .pipe(tap((response) => this.setAuthFromResponse(response)));
   }

   /**
    * Register a new user.
    *
    * NOTE: The backend now returns an authentication response (AuthTokenResponseDto)
    * upon successful registration (access + refresh token). We persist that
    * authentication here by calling setAuthFromResponse so the user is logged in
    * immediately after registering.
    */
   register(credentials: UserRegistration): Observable<AuthTokenResponseDto> {
     return this.http
       .post<AuthTokenResponseDto>(apiUrl('/v1/authentication/register'), credentials)
       .pipe(tap((response) => this.setAuthFromResponse(response)));
   }

  refreshAccessToken(): Observable<AuthTokenResponseDto> {
    if (this.refreshInProgress) {
      // A refresh is already in flight — wait for it instead of firing another
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1)
      ) as Observable<AuthTokenResponseDto>;
    }

    this.refreshInProgress = true;
    this.refreshTokenSubject.next(null); // reset so waiters block until new value arrives

    return this.http.post<AuthTokenResponseDto>(
      apiUrl('/v1/authentication/refresh-token'),
      {},
      { withCredentials: true } // sends the httpOnly refresh cookie
    ).pipe(tap(response => {
        this.setAuthFromResponse(response)
        this.refreshInProgress = false;
        this.refreshTokenSubject.next(response);
      }),
      catchError(err => {
        this.refreshInProgress = false;
        this.refreshTokenSubject.next(null);
        this.purgeAuth();
        this.router.navigate(['/welcome']); // refresh token is dead — force re-login
        return throwError(() => err);
      })
    );
  }

   verifyEmail(token: string): Observable<UserResponseDto> {
     return this.http.get<UserResponseDto>(apiUrl('/v1/authentication/verify-email'), {
       params: { token },
     });
   }

   resendVerificationEmail(email: string): Observable<void> {
     return this.http.post<void>(apiUrl('/v1/authentication/resend-verification-email'), null, {
       params: { email },
     });
   }

   refreshToken(refreshToken: string): Observable<AuthTokenResponseDto> {
     const dto: RefreshTokenDto = { refreshToken };
     return this.http
       .post<AuthTokenResponseDto>(apiUrl('/v1/authentication/refresh-token'), dto)
       .pipe(tap((response) => this.setAuthFromResponse(response)));
   }

  logout(): void {
    this.http.post(apiUrl('/v1/authentication/logout'), {}).subscribe({
      next: () => {
        this.purgeAuth();
        void this.router.navigate(['/welcome']);
      },
      error: () => {
        this.purgeAuth();
        void this.router.navigate(['/welcome']);
      }
    });
  }

  getCurrentUser(): Observable<{ user: User }> {
    return this.http.get<{ user: User }>(apiUrl('/v1/users')).pipe(
      tap({
        next: ({ user }) => this.setAuth(user),
        error: (err: HttpErrorResponse) => this.handleAuthError(err),
      }),
      shareReplay(1),
      catchError(() => EMPTY),
    );
  }

  /**
   * Handle errors from /api/user endpoint
   * - 4XX errors: Token is invalid, logout the user
   * - 5XX/network errors: Server issue, enter "unavailable" mode (keep token, show placeholder)
   */
  private handleAuthError(err: HttpErrorResponse): void {
    const status = err.status;

    if (status >= 400 && status < 500) {
      // 4XX: Client error (invalid token, forbidden, etc.) - logout
      this.purgeAuth();
    } else {
      // 5XX or network error (status 0) - auth temporarily unavailable
      this.setAuthUnavailable();
    }
  }

  /**
   * Set auth state to unavailable (server error, but keep token for retry)
   */
  private setAuthUnavailable(): void {
    this.currentUserSubject.next(null);
    this.authStateSubject.next('unavailable');
    this.scheduleRetry();
  }

  /**
   * Schedule auto-retry with exponential backoff: 2s, 4s, 8s, 16s, 16s, 16s...
   */
  private scheduleRetry(): void {
    this.cancelRetry();

    if (!this.jwtService.getToken()) {
      return; // No token, nothing to retry
    }

    // Calculate delay: 2, 4, 8, 16, 16, 16... (capped at 16s)
    const delaySeconds = Math.min(2 * Math.pow(2, this.retryAttempt), 16);
    this.retryAttempt++;

    this.retrySubscription = timer(delaySeconds * 1000).subscribe(() => {
      if (this.jwtService.getToken()) {
        this.authStateSubject.next('loading');
        this.getCurrentUser().subscribe();
      }
    });
  }

  /**
   * Cancel any pending retry
   */
  private cancelRetry(): void {
    if (this.retrySubscription) {
      this.retrySubscription.unsubscribe();
      this.retrySubscription = null;
    }
  }

  update(user: Partial<User>): Observable<User> {
    return this.http.patch<User>(apiUrl('/v1/users/'), user).pipe(
      tap((updatedUser) => {
        const current = this.currentUserSubject.getValue();
        if (current) {
          this.currentUserSubject.next({ ...current, ...updatedUser });
        }
      }),
    );
  }

   setAuth(user: User): void {
     this.cancelRetry();
     this.retryAttempt = 0;
     this.jwtService.saveToken(user.token);
     this.currentUserSubject.next(user);
     this.authStateSubject.next('authenticated');
   }

   /**
    * Set authentication from AuthTokenResponseDto (login/refresh token response)
    */
   private setAuthFromResponse(response: AuthTokenResponseDto): void {
     const currentUser = this.currentUserSubject.getValue() || {} as Partial<User>;
     const user: User = {
       ...currentUser,
       id: response.id,
       name: response.name,
       email: response.email,
       token: response.token,
       tokenType: response.tokenType,
       activeRole: response.activeRole,
       emailVerifiedAt: response.emailVerifiedAt,
     } as User;
     this.setAuth(user);
   }

  purgeAuth(): void {
    this.cancelRetry();
    this.retryAttempt = 0;
    this.jwtService.destroyToken();
    this.currentUserSubject.next(null);
    this.authStateSubject.next('unauthenticated');
  }
}
